import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ThemeMode = 'light' | 'dark' | 'system'

export interface Preferences {
  rememberPreferences: boolean
  reducedMotion: boolean
  theme: ThemeMode
}

const STORAGE_KEY = 'sa-calculators-preferences'

function readStored(): Partial<Preferences> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Partial<Preferences>) : {}
  } catch {
    return {}
  }
}

function applyTheme(theme: ThemeMode) {
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark)
  document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light')
}

export const usePreferencesStore = defineStore('preferences', () => {
  const stored = readStored()

  const rememberPreferences = ref(stored.rememberPreferences ?? false)
  const reducedMotion = ref(stored.reducedMotion ?? false)
  const theme = ref<ThemeMode>(stored.theme ?? 'system')

  function save() {
    if (rememberPreferences.value) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          rememberPreferences: rememberPreferences.value,
          reducedMotion: reducedMotion.value,
          theme: theme.value,
        })
      )
    }
  }

  function setTheme(value: ThemeMode) {
    theme.value = value
    applyTheme(value)
    save()
  }

  // Apply theme on store creation if running in browser
  if (typeof window !== 'undefined') {
    applyTheme(theme.value)
  }

  return { rememberPreferences, reducedMotion, theme, setTheme, save }
})
